import type { ReactElement } from "react";
import { isValidElement } from "react";

import type { PaginationResponse } from "@/interfaces/api";
import type { PlainObject } from "@/interfaces/common";

import { toast } from "./toast";

export { generateOptionsFromEnum } from "./enum-options";

export function getNestedProps(obj: any, path: string) {
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
}

export function joinPaths(...paths: string[]): string {
  return paths
    .map((path) => path.replace(/(^\/+|\/+$)/g, ""))
    .filter(Boolean)
    .join("/");
}

export function normalizePath(path: string) {
  return `/${path.replace(/^\//, "")}`.replace(/\/+/g, "/");
}

export function removeExtraSpacesAndDiacritics(text: string) {
  return removeDiacritics(text).replace(/\s+/g, " "); // Mantém somente um espaço entre palavras
}

export function removeDiacritics(text: string) {
  return text
    .normalize("NFD") // Decompõe os caracteres acentuados
    .replace(/[\u0300-\u036f]/g, ""); // Remove acentos
}

export function normalizeForSearch(term: string) {
  return removeExtraSpacesAndDiacritics(term).replace(/(^["']+)|(["']+$)/g, ""); // Remove aspas simples e duplas no início e fim
}

export const getSingleTranslation = <T extends PlainObject>(value: T, key: keyof T) => ({
  [key]: value[key],
});

export function getHtmlContent(html: string): string {
  const parser = new DOMParser();
  try {
    return parser.parseFromString(html, "text/html").body.textContent ?? "";
  } catch {
    return "";
  }
}

export function getIcon(icon: string) {
  return new URL(`../../public/icons/${icon}`, import.meta.url).href;
}

export function isElement(element: any): element is ReactElement | null | undefined {
  return !element || isValidElement(element);
}

export async function copyToClipboard(text: string, { toast: alert = true } = {}) {
  try {
    await navigator.clipboard.writeText(text);

    if (alert) {
      toast.success("Texto copiado.");
    }
  } catch {
    toast.error("Erro ao copiar.");
  }
}

export function formatToPaginationResponse<T>(data: T[]): PaginationResponse<T> {
  return {
    data: data,
    total: data.length,
    page: 1,
    pageCount: 1,
    limit: data.length,
  };
}

export const queryBuilder = {
  equal: (...criteria: { field: string; value: any }[]) => ({
    equal: criteria,
  }),
  notEqual: (...criteria: { field: string; value: any }[]) => ({
    notEqual: criteria,
  }),
};

export const withDefaults = <T extends PlainObject>(value: T | undefined, defaultValue: Partial<T>): T => {
  return { ...defaultValue, ...(value ?? {}) } as T;
};

export const formatCPF = (cpf: string) => cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");

export const formatCEP = (cep: string) => cep.replace(/(\d{5})(\d{3})/, "$1-$2");

export const formatPhone = (phone: string) => phone.replace(/(\d{2})(\d{4,5})(\d{4})/, "($1) $2-$3");

/** Remover formatação retornando apenas dígitos */
export const toDigitsOnly = (value?: string | null) => (value ? value.replace(/\D/g, "") : "");

/** Converter string vazia para undefined */
export const convertTrimToUndefined = (value?: string | null) => {
  const t = value?.trim();
  return t || undefined;
};

/** Converter string vazia para null */
export const convertTrimToNull = (value?: string | null) => {
  const t = value?.trim();
  return t || null;
};

const isValueEmpty = (value: any, preserveNulls: boolean): boolean => {
  if (preserveNulls) {
    return value === undefined;
  }
  return value === undefined || value === null;
};

function allFieldsEmpty(obj: unknown) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return false;
  }
  return Object.values(obj as Record<string, unknown>).every((v) => isValueEmpty(v, false));
}

/**
 * Remove campos vazios de um objeto recursivamente  (a menos que preserveNulls === true)
 *
 * Processa objetos, arrays e valores primitivos, removendo:
 * - Strings vazias ou contendo apenas espaços em branco
 * - Números zero em contextos opcionais
 * - Valores null e undefined
 * - Objetos vazios (sem propriedades após limpeza)
 * - Itens nulos/undefined dentro de arrays
 *
 * Mantém:
 * - Arrays vazios como []
 * - Valores booleanos (false é mantido)
 * - Datas (instâncias de Date)
 * - Strings com conteúdo válido
 * - Valores null/undefined (quando preserveNulls === true)
 *
 * @param {any} obj - Objeto, array ou valor primitivo a ser limpo
 * @param {boolean} preserveNulls - Se true, mantém null/undefined no objeto (padrão: false)
 * @returns {any} Objeto/array limpo com campos vazios removidos, ou undefined se ficar vazio
 *
 * @example
 * cleanEmptyFields({ name: "João", email: "", age: 0 })
 * // Retorna: { name: "João" }
 *
 * @example
 * cleanEmptyFields({ name: "João", email: null }, true)
 * // Retorna: { name: "João", email: null }
 *
 * @example
 * cleanEmptyFields({ items: [{ id: 1, name: "" }, { id: 2, name: "Test" }] })
 * // Retorna: { items: [{ id: 1 }, { id: 2, name: "Test" }] }
 *
 * @example
 * cleanEmptyFields({ socialMedias: [] })
 * // Retorna: { socialMedias: [] }
 */
export const cleanEmptyFields = (obj: any, preserveNulls: boolean = false): any => {
  if (obj === null || obj === undefined) {
    return preserveNulls ? obj : undefined;
  }

  if (obj instanceof Date) {
    return obj;
  }

  if (Array.isArray(obj)) {
    // limpa cada item e remove itens "vazios"
    const cleanedArr = obj.map((item) => cleanEmptyFields(item, preserveNulls));

    // retorna [] se o array estiver vazio ou se todos os seus itens forem vazios
    return cleanedArr.filter((v) => !isValueEmpty(v, preserveNulls) && !allFieldsEmpty(v));
  }

  if (typeof obj !== "object") {
    // String vazia → null (se preserveNulls) ou undefined
    if (typeof obj === "string" && obj.trim() === "") {
      return preserveNulls ? null : undefined;
    }
    // Número 0 em contexto de medidas opcionais → null (se preserveNulls) ou undefined
    if (typeof obj === "number" && obj === 0) {
      return preserveNulls ? null : undefined;
    }
    return obj;
  }

  const cleaned: any = {};
  for (const [key, value] of Object.entries(obj)) {
    const cleanedValue = cleanEmptyFields(value, preserveNulls);
    // Só incluir propriedade no objeto se não for vazio (respeitando preserveNulls)
    if (!isValueEmpty(cleanedValue, preserveNulls)) {
      cleaned[key] = cleanedValue;
    }
  }
  return Object.keys(cleaned).length === 0 ? undefined : cleaned;
};

export function joinNames(names: string[], separator = "ou"): string {
  if (!names.some(Boolean)) {
    return "";
  }

  if (names.length === 1) {
    return names[0];
  }

  return `${names.slice(0, -1).join(", ")} ${separator} ${names.slice(-1)}`;
}

export const toBooleanLabel = (value: boolean | null | undefined) => {
  if (value === null || value === undefined) {
    return "-";
  }

  return value ? "Sim" : "Não";
};
